import { fetchBaseUrl } from "core/utils/common";
import React, { useEffect, useReducer, useRef } from "react";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import WorkflowMarkdown from "./workflowMarkdown";
import { storeIncidentAnalysis } from "modules/flywheel/store/incident";

// Expose Redux dispatch globally for our component to use
declare global {
  interface Window {
    reduxDispatch: any;
  }
}

const baseUrl = fetchBaseUrl({ port: 8000 });

// --- State Types ---
interface State {
  content: string;
  loading: boolean;
  error: string | null;
}

type Action =
  | { type: "start" }
  | { type: "append"; payload: string }
  | { type: "error"; payload: string }
  | { type: "done" };

// --- Initial State ---
const initialState: State = {
  content: "",
  loading: false,
  error: null,
};

// --- Reducer ---
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "start":
      return { ...state, content: "", loading: true, error: null };
    case "append":
      return { ...state, content: state.content + action.payload };
    case "error":
      return { ...state, loading: false, error: action.payload };
    case "done":
      return { ...state, loading: false };
    default:
      return state;
  }
}

// --- Thunk Type Support ---
type ThunkAction = (dispatch: Dispatch, getState: () => State) => void;
type Dispatch = (action: Action | ThunkAction) => void;

// --- useThunkReducer Hook ---
function useThunkReducer(
  reducer: (state: State, action: Action) => State,
  initialState: State
): [State, Dispatch] {
  const [state, dispatchBase] = useReducer(reducer, initialState);

  const dispatch: Dispatch = (action) => {
    if (typeof action === "function") {
      action(dispatch, () => state);
    } else {
      dispatchBase(action);
    }
  };

  return [state, dispatch];
}

// --- Thunk: Stream Markdown ---
const streamMarkdownThunk = (url: string, user_input:any, signal: AbortSignal, conversationId: string): ThunkAction => {
  return async (dispatch, getState) => {
    dispatch({ type: "start" });
    
    try {
      const response = await fetch(url, { signal, method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": "dev-key",
      },
      body: JSON.stringify({
        user_input: user_input,
        conversationid: conversationId
      }) });

      if (!response.body) {
        throw new Error("ReadableStream not supported in response.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = ""; // To accumulate all chunks

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        fullContent += chunk; // Accumulate content
        dispatch({ type: "append", payload: chunk });
      }

      // Store the complete content in Redux
      const reduxDispatch = window.reduxDispatch;
      if (reduxDispatch && typeof reduxDispatch === 'function') {
        const inputKey = user_input || '';
        reduxDispatch(storeIncidentAnalysis({
          incidentId: inputKey,
          analysis: fullContent
        }));
      }

      dispatch({ type: "done" });
    } catch (error: any) {
      if (error.name !== "AbortError") {
        dispatch({ type: "error", payload: error.message });
      }
    }
  };
};

// --- Component ---
const StreamMarkdownPage: React.FC<{ user_input: string, conversationId: string }> = ({ user_input, conversationId }) => {
  const [state, dispatchLocal] = useThunkReducer(reducer, initialState);
  const abortControllerRef = useRef<AbortController | null>(null);
  const reduxDispatch = useAppDispatch();
  const incidentState = useAppSelector((state) => state.incidents);
  
  // Make Redux dispatch available globally for the streaming thunk
  useEffect(() => {
    window.reduxDispatch = reduxDispatch;
    return () => {
      window.reduxDispatch = undefined;
    };
  }, [reduxDispatch]);

  useEffect(() => {
    // Skip API call if user_input is empty
    if (!user_input) return;
    
    // Check if we already have this data in Redux
    const userInputKey = user_input || '';
    if (incidentState.incidentAnalysisMap?.[userInputKey]) {
      console.log(`Using cached workflow analysis for ${userInputKey}`);
      console.log(incidentState.incidentAnalysisMap?.[userInputKey]);
      // Use cached content
      dispatchLocal({ type: "start" });
      dispatchLocal({ type: "append", payload: incidentState.incidentAnalysisMap?.[userInputKey] || '' });
      dispatchLocal({ type: "done" });
      return;
    }
    
    console.log(`Fetching workflow analysis for ${userInputKey}`);
    const controller = new AbortController();
    abortControllerRef.current = controller;

    dispatchLocal(streamMarkdownThunk(`${baseUrl}/socai/api/workflow/security_analysis?stream=true`, JSON.stringify(incidentState.pageEvents.filter(e=>e.id==incidentState.selectedRow)[0]), controller.signal, conversationId));

    return () => {
      controller.abort();
    };
  }, [incidentState.selectedRow]);

  return (
    <div className="">
      <h1 className="text-xl font-bold mb-4">Work Flow Analysis</h1>

      {state.loading && (
        <div className="relative w-full h-2 bg-gray-200 rounded overflow-hidden mb-4">
          <div className="absolute top-0 left-0 w-full h-full bg-blue-500 animate-pulse" />
        </div>
      )}

      {state.error && (
        <p className="text-red-500 mb-4">❌ Error: {state.error}</p>
      )}

      <div style={{ 
        overflow: 'auto', 
        wordWrap: 'break-word',
        maxWidth: '100%'
      }}>
        <WorkflowMarkdown content={state.content} />
      </div>
    </div>
  );
};

export default StreamMarkdownPage;
